import styles from "./WaterBodiesTarget.module.css";
import React, { useState, useMemo } from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  Circle,
} from "@react-google-maps/api";
import cn from 'classnames';


type WaterBody = {
  name: string;
  lat: number;
  lng: number;
};

type MapCenter = {
  lat: number;
  lng: number;
};

interface IWaterBodiesTargetProps {
    mapCenter: MapCenter;
    lakeList: WaterBody[];
    mapZoom: number;
    defaultRadius: number;
    className?: string;
};
const containerStyle = {
  width: "100%",
  height: "223px",
};


const milesToMeters = (miles: number) => miles * 1609.34;
function getDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

const WaterBodiesTarget: React.FC<IWaterBodiesTargetProps> = ({lakeList, mapCenter, mapZoom, defaultRadius, className}) => {
  const [radius, setRadius] = useState<number>(defaultRadius);
  const [value, setValue] = useState(defaultRadius);

  const min = 1;
  const max = 100;
  const percentage = ((value - min) / (max - min)) * 100;

  const sliderStyle = {
    background: `linear-gradient(90deg, var(--vividGreen) 0%, var(--vividGreen2) ${percentage}%, rgba(152, 215, 219, .5) ${percentage}%, rgba(221, 219, 255, .5) 100%)`,
  };

  const lakesWithinRadius = useMemo(() => {
    return lakeList.filter(
      (lake) =>
        getDistanceMiles(mapCenter.lat, mapCenter.lng, lake.lat, lake.lng) <=
        radius
    );
  }, [radius]);

  const renderTargetItem = (label: string, value: string, className?: string) => {
    return (
        <div className={cn(styles.targetItem, className)}>
            <div className={styles.targetlabel}>{label}</div>
            <div className={styles.targetValue}>{value}</div>
        </div>
    );
  };

  return (
    <div className={cn(styles.waterBodiesTargetWrap, className)}>
      <div className={cn(styles.waterBodiesTargetCol, styles.colLeft)}>
        <div className={cn(styles.lakeWrap)}>
            <div className={cn(styles.title)}>Water Bodies</div>
            <div className={cn(styles.summary)}>water bodies to send updates about</div>
            <div className={styles.lakeList}>
                {lakesWithinRadius.length > 0 ? (
                    lakesWithinRadius.map((lake) => (
                        <span key={lake.name} className={styles.lakeTag}>
                        {lake.name}
                        </span>
                    ))
                ) : (
                    <div className={styles.noLakeFound}>No lakes found.</div>
                )}
            </div>
        </div>
        <div className={styles.mapWrap}>
            <LoadScript googleMapsApiKey={'AIzaSyAOVYRIgupAurZup5y1PRh8Ismb1A3lLao'}>
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapCenter}
                zoom={mapZoom}
            >
                {lakesWithinRadius.map((lake) => (
                <React.Fragment key={lake.name}>
                    <Marker position={{ lat: lake.lat, lng: lake.lng }} />
                    <Circle
                    center={{ lat: lake.lat, lng: lake.lng }}
                    radius={milesToMeters(radius)}
                    options={{
                        strokeColor: "#4285F4",
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillColor: "#4285F4",
                        fillOpacity: 0.2,
                    }}
                    />
                </React.Fragment>
                ))}
            </GoogleMap>
            </LoadScript>
        </div>
      </div>
      <div className={cn(styles.waterBodiesTargetCol, styles.colRight)}>
        <div className={cn(styles.title)}>Target Radius</div>
        <div className={cn(styles.summary)}>Set targeting parameters for user notifications</div>
        <div className={styles.setTarget}>
            <div className={cn(styles.tageTitle)}><span className={styles.targetRadius}>Target Radius: {radius} Miles</span> (Users within this radius of selected water bodies will receive updates)</div>
            <div className={styles.rangeSlider}>
                <input
                    type="range"
                    min={1}
                    max={100}
                    value={radius}
                    onChange={(e) => {setRadius(Number(e.target.value)); setValue(Number(e.target.value));}}
                    className={styles.rangeInput}
                    style={sliderStyle}
                />
                <div className={styles.sliderValueWrap}>
                    <div className={cn(styles.sliderValueItem, percentage <= 13 && styles.lessValue)}>1 miles</div>
                    <div className={cn(styles.sliderValueItem, styles.sliderCurrentValue)} style={{ left: `${radius}%`}}>{radius} miles</div>
                    <div className={cn(styles.sliderValueItem, percentage >= 80 && styles.greatValue)}>100 miles</div>
                </div>
            </div>
        </div>
        <div className={styles.detailsWrap}>
            <div className={styles.detailTitle}>Target Cities</div>
            {renderTargetItem('New York', 'Chicago')}
            {renderTargetItem('Los Angeles', 'Miami')}
            {renderTargetItem('Seatle', 'Denver')}
        </div>
        <div className={styles.detailsWrap}>
            <div className={styles.detailTitle}>Target Zones</div>
            {renderTargetItem('NorthEast', 'SouthEast')}
            {renderTargetItem('MidWest', 'SouthWest')}
            {renderTargetItem('NothWest', 'Central')}
        </div>
        <div className={styles.targetedUser}>
          Estimated Reach:{" "}
          <span className={styles.targetedUserCount}>
            {lakesWithinRadius.length * 5000} users
          </span>
        </div>
      </div>
    </div>
  );
};

export default WaterBodiesTarget;
